import { z } from "zod";
import { trimmed } from "./schemas/common";

/**
 * "Contact the developer" form (public /contact page and the landing page's contact section).
 * The API mails the message to the operator; nothing is stored in our database.
 *
 * Anti-abuse layers, cheapest first, all enforced by the API (`ContactService`):
 *   same-origin Origin/Referer → per-IP rate limit → honeypot fields → signed nonce (HMAC,
 *   min/max age below) → Cloudflare Turnstile (only when the operator has configured it).
 * The nonce ages are mirrored by the client to know how long to wait before a "too-fast" retry.
 */
export const CONTACT = {
  /** A nonce younger than this is "too fast" — no human fills three fields quicker. */
  minAgeMs: 3_000,
  /** A nonce older than this has expired — the client takes a fresh one and resends. */
  maxAgeMs: 30 * 60 * 1000,
  maxNameChars: 100,
  maxEmailChars: 200,
  maxMessageChars: 5_000,
} as const;

/** What the message is about — routes the subject line, nothing else. */
export const CONTACT_TOPIC = ["general", "support", "bug", "feature", "billing", "business", "privacy"] as const;
export type ContactTopic = (typeof CONTACT_TOPIC)[number];

export const contactMessageSchema = z.object({
  name: trimmed(CONTACT.maxNameChars).min(1),
  email: z.email().trim().max(CONTACT.maxEmailChars),
  topic: z.enum(CONTACT_TOPIC).default("general"),
  message: trimmed(CONTACT.maxMessageChars).min(1),
  /** Signed nonce from GET /contact/token. */
  formToken: z.string().max(200).default(""),
  /** Turnstile response, present only when the widget is configured. */
  turnstileToken: z.string().max(4_096).optional(),
  /** Honeypots: real people never see these fields; anything in them means a bot. */
  website: z.string().max(500).optional(),
  company: z.string().max(500).optional(),
});
export type ContactMessageInput = z.infer<typeof contactMessageSchema>;

export interface ContactTokenDTO {
  token: string;
  /** Whether the API will demand a Turnstile response (the page then has to render the widget). */
  humanCheck: boolean;
}
export interface ContactResultDTO {
  ok: true;
}
